import { rcLogger } from '@readcloud/common';
import { tryCatchAndNotify } from '@readcloud/common/lib/rum';
import { PDFDataRangeTransport } from 'pdfjs-dist';
import { tauriFetch } from '@readcloud/fetch-wrapper';

const extractRangeFromHeader = (rangeResponseHeader: string) => {
  const beginMatches = rangeResponseHeader.match(/^bytes ([0-9]*)\-/);
  const endMatches = rangeResponseHeader.match(/\-([0-9]*)/);
  return {
    begin:
      beginMatches && beginMatches.length > 1
        ? parseInt(beginMatches[1], 10)
        : 0,
    end: endMatches && endMatches.length > 1 ? parseInt(endMatches[1], 10) : 0,
  };
};

const defaultHeaderKeys = {
  contentRange: 'Content-Range',
  range: 'range',
};

export class CustomPDFDataRangeTransport extends PDFDataRangeTransport {
  public abortControllers: AbortController[] = [];
  constructor(
    public url: string,
    public length: number,
    public headerKeys = defaultHeaderKeys,
    public onError: () => void,
    public isLoading: () => boolean,
    public preprocessChunk?: (
      chunk: ArrayBuffer,
      counter: number
    ) => Promise<ArrayBuffer | Uint8Array>,
    public setNewProgress?: (progress: number) => void,
    public options?: {
      initialData?: Uint8Array;
    },
    public blockSize = 16
  ) {
    super(length, options ? options.initialData : undefined);
  }
  public abort() {
    this.abortControllers.forEach((a, i) => {
      a.abort();
    });

    this.abortControllers = [];
  }
  /**
   * Tauri's asset protocol URLs must be fetched via the webview's native fetch,
   * not through the Tauri HTTP plugin (which only handles http/https). Detect
   * the asset URL shapes that `convertFileSrc` produces on each platform.
   */
  private isAssetProtocolUrl(): boolean {
    return (
      this.url.startsWith('asset://') ||
      this.url.startsWith('http://asset.localhost') ||
      this.url.startsWith('https://asset.localhost')
    );
  }

  public async requestDataRange(beginRaw: number, end: number) {
    const requestHeaders = new Headers();
    const begin = this.preprocessChunk
      ? beginRaw - (beginRaw % this.blockSize)
      : beginRaw;

    requestHeaders.set(this.headerKeys.range, `bytes=${begin}-${end - 1}`);
    requestHeaders.set('accept', '*/*');
    // requestHeaders.set("accept-encoding", "gzip, deflate, br");
    // requestHeaders.set("x-rcsauth", "Bearer " + getAccessToken());

    rcLogger.debug('Getting Data Range: Started');

    const abortController = new AbortController();
    this.abortControllers.push(abortController);

    const fetchImpl = this.isAssetProtocolUrl()
      ? window.fetch.bind(window)
      : tauriFetch;

    let response = undefined;
    let buffer = undefined;
    let contentRangeHeaderValue = '';
    await tryCatchAndNotify(
      async () => {
        response = await fetchImpl(this.url, {
          headers: {
            [this.headerKeys.range]: `bytes=${begin}-${end - 1}`,
            accept: '*/*',
          },
          method: 'GET',
          mode: 'cors',
          redirect: 'follow',
          signal: abortController.signal,
        });

        //get content range header value
        contentRangeHeaderValue = response.headers.get(
          this.headerKeys.contentRange
        );

        //parse the response data into a buffer
        buffer = await response.arrayBuffer();
        this.setNewProgress &&
          this.setNewProgress(((end - begin) / this.length) * 100);
        this.abortControllers = this.abortControllers.filter(
          (i) => i !== abortController
        );

        if (!contentRangeHeaderValue) {
          throw new Error(
            `${this.headerKeys.contentRange} header not present in response headers`
          );
        }

        const range = extractRangeFromHeader(contentRangeHeaderValue);

        let uint8Vbuffer = new Uint8Array(buffer);

        super.onDataRange(
          range.begin,
          //@ts-expect-error
          this.preprocessChunk
            ? await this.preprocessChunk(
                uint8Vbuffer.buffer,
                Math.floor(begin / this.blockSize)
              ).then((chunk) => chunk.slice(beginRaw - begin))
            : uint8Vbuffer
        );
      },
      async (e) => {
        this.onError();
      }
    );
  }
}
